import React from 'react';
import { Link } from 'wouter';
import FilterSection from '../../../pages/DesignSystem/patterns/FilterDrawer/FilterSection';
import { Icon } from '../../../pages/DesignSystem/primitives/Icon';
import Button from '../../../pages/DesignSystem/primitives/Button';
import SearchBar from '../../../pages/DesignSystem/primitives/SearchBar';
import TagList from '../../../pages/DesignSystem/primitives/TagList';
import LocationFilter from '../../../pages/DesignSystem/patterns/LocationFilter';
import DateTimeFilters from '../../../pages/DesignSystem/patterns/DateTimeFilters';
import DistanceSlider from '../../../pages/DesignSystem/primitives/DistanceSlider';
import LogoSVG from '../../../pages/DesignSystem/primitives/LogoSVG';
import { PREDEFINED_TAGS } from '../../../pages/DesignSystem/types/tags.types';
import './FilterSidebar.css';

export interface FilterSidebarProps {
  // Where
  searchCity?: string;
  setSearchCity?: (city: string) => void;
  currentLocation?: { latitude: number; longitude: number };
  isLoadingLocation?: boolean;
  onLocationChange?: (location: {
    latitude: number;
    longitude: number;
    shouldCenter?: boolean;
  }) => void;
  distance?: number;
  onDistanceChange?: (distance: number) => void;
  // When
  selectedDates?: string[];
  onDateChange?: (dates: string[]) => void;
  selectedTimes?: string[];
  onTimeChange?: (times: string[]) => void;
  // Date range
  dateRangeStart?: Date | null;
  dateRangeEnd?: Date | null;
  onDateRangeChange?: (start: Date | null, end: Date | null) => void;
  showDateRange?: boolean;
  // What
  searchText?: string;
  onSearchTextChange?: (text: string) => void;
  selectedTags?: string[];
  onTagSelect?: (tagId: string) => void;
  // View mode
  viewMode?: 'list' | 'map';
  // Reset function
  onReset: () => void;
}

const FilterSidebar: React.FC<FilterSidebarProps> = ({
  // Where
  searchCity = "",
  setSearchCity = () => {},
  currentLocation = undefined,
  isLoadingLocation = false,
  onLocationChange = () => {},
  distance = 5,
  onDistanceChange = () => {},
  // When
  selectedDates = [],
  onDateChange = () => {},
  selectedTimes = [],
  onTimeChange = () => {},
  // Date range
  dateRangeStart = null,
  dateRangeEnd = null,
  onDateRangeChange = () => {},
  showDateRange = true,
  // What
  searchText = "",
  onSearchTextChange = () => {},
  selectedTags = [],
  onTagSelect = () => {},
  // View mode
  viewMode = 'list',
  // Reset function
  onReset = () => {},
}) => {
  // Fonction de réinitialisation
  const handleReset = () => {
    setSearchCity("");
    onSearchTextChange("");
    if (viewMode !== 'list') {
      onDistanceChange(5);
    }
    onDateChange([]);
    onTimeChange([]);
    onDateRangeChange(null, null);
    selectedTags.forEach(tag => onTagSelect(tag));
    onReset();
  };

  // Compteur de filtres actifs
  const activeFiltersCount = [
    searchCity.length > 0,
    searchText.length > 0,
    selectedDates.length > 0,
    selectedTimes.length > 0,
    selectedTags.length > 0,
    distance !== 5,
    dateRangeStart !== null || dateRangeEnd !== null
  ].filter(Boolean).length;

  return (
    <div className="filter-sidebar">
      {/* Navigation menu */}
      <nav className="filter-sidebar__nav">
        <Link href="/organizer/dashboard" className="filter-sidebar__nav-link">
          <Icon name="LayoutDashboard" size={16} />
          <span>Organizer</span>
        </Link>
        <Link href="/legal" className="filter-sidebar__nav-link">
          <Icon name="FileText" size={16} />
          <span>Legal</span>
        </Link>
      </nav>

      {/* Header avec logo */}
      <div className="filter-sidebar__header">
        <LogoSVG
          variant="what2Do"
          size="sm"
          color="var(--color-primary)"
        />
        <h2 className="filter-sidebar__title">Filters</h2>
        {activeFiltersCount > 0 && (
          <span className="filter-sidebar__count">{activeFiltersCount}</span>
        )}
      </div>

      <div className="filter-sidebar__content">
        {/* WHERE Section */}
        <FilterSection
          title="Where to go?"
          icon={<Icon name="MapPin" size={20} />}
        >
          <div className="filter-sidebar__location-filters">
            <LocationFilter
              onLocationChange={onLocationChange}
              isLoading={isLoadingLocation}
              currentLocation={currentLocation}
            />
            {viewMode === 'list' && (
              <DistanceSlider
                value={distance}
                onChange={onDistanceChange}
              />
            )}
          </div>
        </FilterSection>

        {/* WHEN Section */}
        <FilterSection
          title="When to go?"
          icon={<Icon name="Clock" size={20} />}
        >
          <DateTimeFilters
            onDateFilterChange={onDateChange}
            onTimeFilterChange={onTimeChange}
            selectedDates={selectedDates}
            selectedTimes={selectedTimes}
            dateRangeStart={dateRangeStart}
            dateRangeEnd={dateRangeEnd}
            onDateRangeChange={onDateRangeChange}
            showDateRange={showDateRange}
          />
        </FilterSection>

        {/* WHAT Section */}
        <FilterSection
          title="What to do?"
          icon={<Icon name="LocateFixed" size={20} />}
        >
          <div className="filter-sidebar__what-filters">
            <SearchBar
              query={searchText}
              onQueryChange={onSearchTextChange}
              placeholder="Search for an activity..."
            />
            <TagList
              tags={PREDEFINED_TAGS}
              selectedTags={selectedTags}
              onTagSelect={onTagSelect}
              size="md"
            />
          </div>
        </FilterSection>
      </div>

      {/* Footer avec bouton reset */}
      <div className="filter-sidebar__footer">
        <Button
          variant="secondary"
          size="sm"
          onClick={handleReset}
          disabled={activeFiltersCount === 0}
        >
          Reset all filters
        </Button>
      </div>
    </div>
  );
};

export default FilterSidebar;
